import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Package, ArrowRight, Truck, PhoneCall, Printer, Leaf, Circle, MapPin, Clock, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api-client';
import { OrderDetails, Shipment, ShipmentStatus, Invoice } from '@shared/types';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrency } from '@/hooks/use-currency';

const MILESTONE_ORDER = ['pending', 'picked_up', 'in_transit', 'out_for_delivery', 'delivered'] as const;
const MILESTONE_LABELS: Record<string, string> = {
  pending: 'Preparing',
  picked_up: 'Shipped',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered'
};
const MILESTONE_PERCENTAGE: Record<number, number> = { 0: 30, 1: 50, 2: 70, 3: 85, 4: 100 };

function getMilestoneIndex(status: ShipmentStatus): number {
  return MILESTONE_ORDER.indexOf(status as typeof MILESTONE_ORDER[number]);
}

function getMilestoneProgress(events: { status: ShipmentStatus }[]): number {
  if (!events.length) return 30;
  const latestStatus = events[events.length - 1]?.status || 'pending';
  const index = getMilestoneIndex(latestStatus);
  return MILESTONE_PERCENTAGE[index] || 30;
}

function formatTimestamp(ts: number) {
  return new Date(ts).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit'
  });
}

const STATUS_ICONS: Record<string, React.ElementType> = {
  pending: Package,
  picked_up: Package,
  in_transit: Truck,
  out_for_delivery: Truck,
  delivered: CheckCircle2,
  failed: AlertCircle
};

export function OrderSuccessPage() {
  const { id } = useParams<{ id: string }>();
  const { countries } = useCurrency();
  const { data: order, isLoading } = useQuery({
    queryKey: ['order-details', id],
    queryFn: () => api<OrderDetails>(`/api/orders/${id}/details`),
    enabled: !!id,
  });
  const { data: shipments } = useQuery({
    queryKey: ['order-shipments', id],
    queryFn: () => api<{ items: Shipment[] }>(`/api/orders/${id}/shipments`),
    enabled: !!id,
  });
  const { data: invoice } = useQuery({
    queryKey: ['order-invoice', id],
    queryFn: () => api<Invoice>(`/api/invoices/by-order/${id}`),
    enabled: !!id,
  });
  const handlePrint = () => window.print();
  const handleDownloadInvoice = () => {
    if (invoice) {
      const blob = new Blob([JSON.stringify(invoice, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20 space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <h1 className="text-lg font-bold">Record not found</h1>
        <Button asChild className="mt-4" variant="outline"><Link to="/">Home</Link></Button>
      </div>
    );
  }
  const orderCountry = countries.find(
    (country) => country.name.toLowerCase() === (order.shippingDetails?.country || '').toLowerCase()
  );
  const orderCurrency = orderCountry?.currency || 'GHS';
  const orderRate = orderCountry?.exchangeRate || 15.6;
  const orderLocale = orderCountry?.locale || 'en-GH';
  const supportLine = orderCountry?.code === 'GH' ? '+233' : orderCountry?.code === 'NG' ? '+234' : '+233';
  const regionalHub = orderCountry?.code === 'GH'
    ? 'Accra Central'
    : orderCountry?.code === 'NG'
      ? 'Lagos Island'
      : `${order.shippingDetails?.city || 'Regional'} Hub`;

  const formatOrderPrice = (amount: number) => {
    const converted = amount * orderRate;
    return new Intl.NumberFormat(orderLocale, {
      style: 'currency',
      currency: orderCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(converted);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="print:hidden">
        <Navbar />
      </div>
      <div className="max-w-3xl mx-auto px-4 py-12 space-y-8">
        <div className="text-center space-y-3 print:hidden">
          <div className="inline-flex items-center justify-center p-2 bg-green-50 text-green-600 rounded-full border border-green-200">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-bold">Confirmation Successful</h1>
          <p className="text-sm text-muted-foreground">Reference: #{order.id.slice(0, 8)}</p>
          {invoice ? (
            <Button variant="ghost" size="sm" onClick={handleDownloadInvoice} className="text-xs h-7">
              <Printer className="mr-2 h-3 w-3" /> Download Invoice
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={handlePrint} className="text-xs h-7">
              <Printer className="mr-2 h-3 w-3" /> Print Receipt
            </Button>
          )}
        </div>
        <div className="border rounded-md bg-white p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
              <Package className="h-3 w-3" /> Manifest
            </h3>
            <div className="divide-y border-t border-b">
              {(order.lineItems || []).map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 text-xs">
                  <span>{item.quantity}x {item.product.name}</span>
                  <span className="font-bold">{formatOrderPrice(item.subtotal)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="text-sm font-bold uppercase">Settled Total</span>
              <span className="text-lg font-bold text-primary">{formatOrderPrice(order.totalPrice)}</span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 border-t pt-6">
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-2">
                <Truck className="h-3 w-3" /> Shipping Address
              </div>
              <div className="text-xs space-y-0.5">
                <p className="font-bold">{order.shippingDetails?.fullname}</p>
                <p className="text-muted-foreground">{order.shippingDetails?.address}</p>
                <p className="text-muted-foreground">{order.shippingDetails?.city}, {order.shippingDetails?.country}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase text-muted-foreground">Regional Hub</div>
              <p className="text-xs font-bold">{regionalHub}</p>
              <p className="text-[10px] text-muted-foreground">Standard dispatch timeframe: 48-72 Hours.</p>
            </div>
          </div>
        </div>
        {shipments?.items?.length > 0 && shipments.items[0].events?.length > 0 && (
          <div className="border rounded-md bg-white p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                <Truck className="h-3 w-3" /> Shipment Tracking
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{getMilestoneProgress(shipments.items[0].events)}%</span>
                <div className="w-16 h-1.5 bg-muted rounded-full">
                  <div className="h-1.5 bg-green-600 rounded-full transition-all" style={{ width: `${getMilestoneProgress(shipments.items[0].events)}%` }} />
                </div>
              </div>
            </div>
            {shipments.items[0].trackingNumber && (
              <p className="text-xs font-mono">Tracking: {shipments.items[0].trackingNumber}</p>
            )}
            <div className="relative">
              <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-muted" />
              <div className="space-y-4">
                {shipments.items[0].events.map((event, idx) => {
                  const Icon = STATUS_ICONS[event.status] || Circle;
                  const isActive = idx === shipments.items[0].events.length - 1;
                  const isException = event.status === 'failed';
                  return (
                    <div key={idx} className="relative flex gap-3 pl-2">
                      <div className={`relative z-10 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                        isException ? 'bg-red-100 text-red-600' : isActive ? 'bg-green-600 text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Icon className="h-3 w-3" />
                      </div>
                      <div className={`flex-1 pb-2 ${isException ? 'text-red-700' : ''}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold">{MILESTONE_LABELS[event.status] || event.status}</span>
                          {isActive && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">Current</span>}
                          {isException && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full">Exception</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{formatTimestamp(event.timestamp)}</span>
                          {event.location && <span className="flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{event.location}</span>}
                        </div>
                        {event.description && <p className="text-xs mt-1 text-muted-foreground">{event.description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div className="bg-muted/50 p-6 rounded-md border space-y-4 print:hidden">
          <h3 className="text-sm font-bold uppercase tracking-tight">Logistics Protocol</h3>
          <div className="space-y-3 text-xs">
             <p>1. <strong>Verification:</strong> A logistics officer will contact you within 6 hours to confirm delivery availability.</p>
             <p>2. <strong>Guides:</strong> Technical startup manuals have been queued for transmission to your email.</p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" className="flex-1 text-xs" asChild>
              <a href={`tel:${supportLine}`} className="flex items-center justify-center gap-2">
                <PhoneCall className="h-3 w-3" /> Support Line
              </a>
            </Button>
            <Button size="sm" className="flex-1 text-xs" asChild>
              <Link to="/my-farm-guide">
                <Leaf className="mr-1 h-3 w-3" /> Get Started
              </Link>
            </Button>
            <Button size="sm" variant="ghost" className="flex-1 text-xs" asChild>
              <Link to="/">
                Home <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}